import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'papers', element: <GeneralPapers /> },
        { path: 'stats', element: <GeneralStats /> },
        { path: 'profile', element: <GeneralProfile /> },
        { path: 'profile/:name', element: <GeneralProfile /> },
        { path: 'settings', element: <GeneralSettings /> },
        { path: 'papers/paper/:title', element: <PaperReview /> },
        { path: 'papers/new', element: <NewPaper /> },

        {
          path: 'reviewer',
          children: [
            { element: <Navigate to="/dashboard/reviewer/find" replace />, index: true },
            { path: 'find', element: <ReviewerFind /> },
            { path: 'active', element: <ReviewerActive /> },
            { path: 'past', element: <ReviewerPast /> },
            { path: 'stats', element: <ReviewerStats /> },
            { path: 'paper/:title', element: <ReviewerPaper /> },
          ],
        },
        {
          path: 'author',
          children: [
            { element: <Navigate to="/dashboard/author/submitted" replace />, index: true },
            { path: 'submitted', element: <AuthorSubmitted /> },
            { path: 'reviewed', element: <AuthorReviewed /> },
            { path: 'stats', element: <AuthorStats /> },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'about', element: <About /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [{ path: '/', element: <Navigate to="/dashboard/papers" /> }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralPapers = Loadable(lazy(() => import('../pages/dashboard/GeneralReviewedPapers')));
const GeneralStats = Loadable(lazy(() => import('../pages/dashboard/GeneralStats')));
const GeneralProfile = Loadable(lazy(() => import('../pages/dashboard/GeneralProfile')));
const GeneralSettings = Loadable(lazy(() => import('../pages/dashboard/GeneralSettings')));

// Reviewer
const ReviewerFind = Loadable(lazy(() => import('../pages/dashboard/ReviewerFind')));
const ReviewerActive = Loadable(lazy(() => import('../pages/dashboard/ReviewerActive')));
const ReviewerPast = Loadable(lazy(() => import('../pages/dashboard/ReviewerPast')));
const ReviewerStats = Loadable(lazy(() => import('../pages/dashboard/ReviewerStats')));
const ReviewerPaper = Loadable(lazy(() => import('../pages/dashboard/ReviewerPaper')));

// Author
const AuthorSubmitted = Loadable(lazy(() => import('../pages/dashboard/AuthorSubmitted')));
const AuthorReviewed = Loadable(lazy(() => import('../pages/dashboard/AuthorReviewed')));
const AuthorStats = Loadable(lazy(() => import('../pages/dashboard/AuthorStats')));

// Paper
const PaperReview = Loadable(lazy(() => import('../pages/dashboard/PaperFinalReview')));
const NewPaper = Loadable(lazy(() => import('../pages/dashboard/NewPaper')));

// MAIN
// const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
// const Contact = Loadable(lazy(() => import('../pages/Contact')));
// const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
